import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Table,
  Select,
  Space,
  Spin,
  Empty,
  Tag,
  Typography,
  Popconfirm,
} from "antd";
import {
  ShopOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { QRCodeSVG } from "qrcode.react";
import PageHeader from "./common/PageHeader";
import VisitLog from "./common/VisitLog";
import { notify, serverError } from "../utils/notify";
import { useAuth } from "../context/AuthContext";
import {
  getShop,
  updateShop,
  deleteShop,
  assignDeviceToShop,
  unassignDeviceFromShop,
  listCameras,
} from "../network/api";
import { relativeTime } from "../constants/health";

const { Text } = Typography;
const FIELDS = ["code", "title", "address", "city", "region", "channel"];

export default function ShopDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { isHardwareAdmin, isSuperAdmin } = useAuth();

  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [assignOptions, setAssignOptions] = useState([]);
  const [assignValue, setAssignValue] = useState();
  const [assigning, setAssigning] = useState(false);

  const fetchShop = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getShop(id);
      setShop(data);
      form.setFieldsValue(FIELDS.reduce((a, k) => ({ ...a, [k]: data[k] ?? "" }), {}));
      setDirty(false);
    } catch (error) {
      if (error?.response?.status === 404) setNotFound(true);
      else notify.error(serverError(error, "Failed to load outlet"));
    } finally {
      setLoading(false);
    }
  }, [id, form]);

  // Devices available to assign (not already in this outlet).
  const fetchAssignable = useCallback(async () => {
    try {
      const res = await listCameras({ page: 1, pageSize: 500 });
      const inThisShop = new Set((shop?.devices || []).map((d) => d.deviceId));
      setAssignOptions(
        (res.data || [])
          .filter((c) => !inThisShop.has(c.deviceId))
          .map((c) => ({ value: c._id, label: `${c.deviceId}${c.deviceTitle ? ` — ${c.deviceTitle}` : ""}${c.shop ? " (reassign)" : ""}` }))
      );
    } catch (e) {
      setAssignOptions([]);
    }
  }, [shop]);

  useEffect(() => {
    if (id) fetchShop();
  }, [id, fetchShop]);

  useEffect(() => {
    if (shop && isHardwareAdmin) fetchAssignable();
  }, [shop, isHardwareAdmin, fetchAssignable]);

  const onSave = async (values) => {
    setSaving(true);
    try {
      const updated = await updateShop(id, values);
      if (updated) setShop((s) => ({ ...s, ...updated }));
      notify.success("Outlet saved");
      setDirty(false);
    } catch (error) {
      const msg = error?.response?.status === 409 ? "An outlet with this code already exists" : null;
      notify.error(msg || serverError(error, "Failed to save outlet"));
    } finally {
      setSaving(false);
    }
  };

  const onAssign = async () => {
    if (!assignValue) return notify.warning("Select a device first");
    setAssigning(true);
    try {
      await assignDeviceToShop(id, { cameraId: assignValue });
      notify.success("Device assigned");
      setAssignValue(undefined);
      fetchShop();
    } catch (error) {
      notify.error(serverError(error, "Failed to assign device"));
    } finally {
      setAssigning(false);
    }
  };

  const onUnassign = async (cameraId) => {
    try {
      await unassignDeviceFromShop(id, cameraId);
      notify.success("Device detached");
      fetchShop();
    } catch (error) {
      notify.error(serverError(error, "Failed to detach device"));
    }
  };

  const onDelete = async () => {
    try {
      await deleteShop(id);
      notify.success("Outlet deleted");
      navigate("/dashboard/outlets");
    } catch (error) {
      notify.error(serverError(error, "Failed to delete outlet"));
    }
  };

  if (loading) {
    return <div style={{ padding: 60, textAlign: "center" }}><Spin size="large" /></div>;
  }
  if (notFound || !shop) {
    return (
      <div>
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate("/dashboard/outlets")} style={{ paddingLeft: 0, marginBottom: 12 }}>
          Back to Outlets
        </Button>
        <Empty description="Outlet not found" />
      </div>
    );
  }

  const deviceColumns = [
    { title: "Device ID", dataIndex: "deviceId", render: (v, r) => <a onClick={() => navigate(`/dashboard/device-details/${r._id}`)}>{v}</a> },
    { title: "Title", dataIndex: "deviceTitle", render: (v) => v || "—" },
    { title: "Network", dataIndex: "currentIp", render: (v, r) => (r.currentNetwork || v ? `${r.currentNetwork || ""} ${v ? `(${v})` : ""}`.trim() : "—") },
    { title: "Updated", dataIndex: "updatedAt", render: (v) => (v ? relativeTime(v) : "—") },
    ...(isHardwareAdmin
      ? [{
          title: "",
          key: "x",
          align: "right",
          render: (_, r) => (
            <Popconfirm title="Detach this device from the outlet?" onConfirm={() => onUnassign(r._id)} okText="Detach">
              <Button type="link" danger icon={<DisconnectOutlined />}>Detach</Button>
            </Popconfirm>
          ),
        }]
      : []),
  ];

  return (
    <div>
      <PageHeader
        title="Outlet"
        subtitle={shop.title || shop.code}
        icon={<ShopOutlined />}
        extra={
          <Space>
            {isSuperAdmin && (
              <Popconfirm title="Delete this outlet? Devices will be detached." okText="Delete" okButtonProps={{ danger: true }} onConfirm={onDelete}>
                <Button danger icon={<DeleteOutlined />}>Delete</Button>
              </Popconfirm>
            )}
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/dashboard/outlets")}>Back</Button>
          </Space>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15}>
          <Card title="Outlet Information">
            <Form form={form} layout="vertical" onFinish={onSave} onValuesChange={() => setDirty(true)} requiredMark={false} disabled={!isHardwareAdmin}>
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item name="code" label="Code" rules={[{ required: true, message: "Required" }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={16}>
                  <Form.Item name="title" label="Name" rules={[{ required: true, message: "Required" }]}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="address" label="Address"><Input /></Form.Item>
              <Row gutter={16}>
                <Col xs={24} sm={8}><Form.Item name="city" label="City"><Input /></Form.Item></Col>
                <Col xs={24} sm={8}><Form.Item name="region" label="Region / Zone"><Input /></Form.Item></Col>
                <Col xs={24} sm={8}><Form.Item name="channel" label="Channel"><Input /></Form.Item></Col>
              </Row>
              {isHardwareAdmin && (
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} disabled={!dirty}>Save changes</Button>
                  <Button icon={<ReloadOutlined />} onClick={() => { form.setFieldsValue(FIELDS.reduce((a, k) => ({ ...a, [k]: shop[k] ?? "" }), {})); setDirty(false); }} disabled={!dirty || saving}>Reset</Button>
                  {dirty && <Text type="warning" style={{ fontSize: 12 }}>Unsaved changes</Text>}
                </Space>
              )}
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={9}>
          <Card title="Outlet QR" style={{ textAlign: "center" }}>
            <div style={{ background: "#fff", padding: 12, display: "inline-block", borderRadius: 8 }}>
              <QRCodeSVG value={shop.qrPayload || shop.code} size={150} level="M" />
            </div>
            <div style={{ marginTop: 12 }}>
              <Text strong style={{ fontSize: 16 }}>{shop.code}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>Scan in the field to identify this outlet</Text>
            </div>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={`Devices in this outlet (${shop.devices?.length || 0})`}
            extra={
              isHardwareAdmin && (
                <Space>
                  <Select
                    showSearch
                    placeholder="Assign a device…"
                    style={{ width: 280 }}
                    value={assignValue}
                    onChange={setAssignValue}
                    options={assignOptions}
                    optionFilterProp="label"
                    notFoundContent="No devices available"
                  />
                  <Button type="primary" icon={<PlusOutlined />} loading={assigning} onClick={onAssign}>Assign</Button>
                </Space>
              )
            }
          >
            <Table
              rowKey="_id"
              size="small"
              columns={deviceColumns}
              dataSource={shop.devices || []}
              pagination={false}
              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No devices assigned" /> }}
            />
          </Card>
        </Col>

        <Col span={24}>
          <VisitLog shop={id} title="Visits at this outlet" />
        </Col>
      </Row>
    </div>
  );
}
